import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import api, { fmt, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { UserPlus, Wrench, Pencil, Trash2, Ban, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

const RANKS = ["Apprentice", "Mechanic", "Senior Mechanic", "Lead Tuner"];

const emptyForm = { name: "", email: "", password: "", rank: "Mechanic", commission_rate: 25, can_payout: false, can_view_sales: false };

export default function Mechanics() {
  const [mechanics, setMechanics] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/mechanics").then((r) => setMechanics(r.data));
  useEffect(() => {
    load();
  }, []);

  const openAdd = () => {
    setEditing(null);
    setForm(emptyForm);
    setOpen(true);
  };
  const openEdit = (m) => {
    setEditing(m);
    setForm({ name: m.name, email: m.email, password: "", rank: m.rank, commission_rate: m.commission_rate, can_payout: !!m.can_payout, can_view_sales: !!m.can_view_sales, lifetime_sales: m.lifetime_sales, owed: m.unpaid_commission });
    setOpen(true);
  };

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (editing) {
        const payload = { name: form.name, rank: form.rank, commission_rate: Number(form.commission_rate), can_payout: !!form.can_payout, can_view_sales: !!form.can_view_sales };
        if (form.password) payload.password = form.password;
        await api.put(`/mechanics/${editing.id}`, payload);
        const lt = Number(form.lifetime_sales);
        const owed = Number(form.owed);
        if (lt !== editing.lifetime_sales || owed !== editing.unpaid_commission) {
          await api.post(`/mechanics/${editing.id}/balances`, { lifetime_sales: lt, owed });
        }
        toast.success("Mechanic updated");
      } else {
        await api.post("/mechanics", { ...form, commission_rate: Number(form.commission_rate), can_payout: !!form.can_payout, can_view_sales: !!form.can_view_sales });
        toast.success("Mechanic added to the roster");
      }
      setOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
    setBusy(false);
  };

  const toggleAccess = async (m) => {
    const suspending = m.active !== false;
    if (suspending && !window.confirm(`Suspend ${m.name}? They won't be able to sign in until you restore access.`)) return;
    await api.put(`/mechanics/${m.id}`, { active: !suspending });
    toast.success(suspending ? `${m.name} suspended` : `${m.name} reactivated`);
    load();
  };

  const remove = async (m) => {
    if (!window.confirm(`Remove ${m.name} from the roster? Their sales history stays.`)) return;
    await api.delete(`/mechanics/${m.id}`);
    toast.success("Mechanic removed");
    load();
  };

  return (
    <div data-testid="mechanics-page">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
        <div>
          <div className="text-[10px] tracking-[0.4em] text-primary mb-2 flex items-center gap-3">
            <span className="h-px w-8 bg-primary/60" />
            CREW ROSTER <span className="font-jp text-muted-foreground">整備士管理</span>
          </div>
          <h1 className="font-display text-2xl sm:text-3xl font-black tracking-tight uppercase">Mechanics</h1>
        </div>
        <Button
          data-testid="add-mechanic-btn"
          onClick={openAdd}
          className="bg-primary text-primary-foreground hover:bg-primary/90 font-display text-[11px] tracking-widest rounded-sm h-11 px-5"
        >
          <UserPlus size={14} className="mr-2" /> ADD MECHANIC
        </Button>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="mechanics-grid">
        {mechanics.map((m, i) => (
          <motion.div
            key={m.id}
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: i * 0.06 }}
            className={`bg-card border rounded-md p-5 card-glow transition-all duration-200 ${m.active === false ? "border-destructive/30 opacity-60" : "border-border"}`}
            data-testid={`mechanic-card-${m.id}`}
          >
            <div className="flex items-start justify-between mb-4">
              <div className="w-11 h-11 rounded-sm bg-secondary border border-primary/30 flex items-center justify-center text-primary">
                <Wrench size={18} />
              </div>
              <div className="flex gap-1">
                <button
                  data-testid={`mechanic-toggle-${m.id}`}
                  onClick={() => toggleAccess(m)}
                  title={m.active === false ? "Restore access" : "Suspend access"}
                  className={`p-1.5 transition-colors ${m.active === false ? "text-destructive hover:text-primary" : "text-muted-foreground hover:text-amber-400"}`}
                >
                  {m.active === false ? <ShieldCheck size={13} /> : <Ban size={13} />}
                </button>
                <button data-testid={`mechanic-edit-${m.id}`} onClick={() => openEdit(m)} className="p-1.5 text-muted-foreground hover:text-primary transition-colors">
                  <Pencil size={13} />
                </button>
                <button data-testid={`mechanic-delete-${m.id}`} onClick={() => remove(m)} className="p-1.5 text-muted-foreground hover:text-destructive transition-colors">
                  <Trash2 size={13} />
                </button>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <div className="text-sm font-bold text-foreground">{m.name}</div>
              <span
                data-testid={`mechanic-status-${m.id}`}
                className={`text-[8px] tracking-[0.2em] px-1.5 py-0.5 rounded-sm border ${m.active === false ? "border-destructive/50 text-destructive" : "border-emerald-400/40 text-emerald-400"}`}
              >
                {m.active === false ? "SUSPENDED" : "ACTIVE"}
              </span>
            </div>
            <div className="text-[10px] tracking-widest text-primary uppercase mt-0.5">{m.rank} • {m.commission_rate}%</div>
            <div className="text-[10px] text-muted-foreground mt-0.5 truncate">{m.email}</div>
            <div className="grid grid-cols-3 gap-2 mt-4 pt-4 border-t border-border text-center">
              <div>
                <div className="text-[9px] tracking-widest text-muted-foreground">SALES</div>
                <div className="font-display text-sm font-bold text-foreground mt-1">{m.sales_count}</div>
              </div>
              <div>
                <div className="text-[9px] tracking-widest text-muted-foreground">LIFETIME</div>
                <div className="font-display text-sm font-bold text-foreground mt-1">{fmt(m.lifetime_sales)}</div>
              </div>
              <div>
                <div className="text-[9px] tracking-widest text-muted-foreground">OWED</div>
                <div className={`font-display text-sm font-bold mt-1 ${m.unpaid_commission > 0 ? "text-amber-400" : "text-muted-foreground"}`}>
                  {fmt(m.unpaid_commission)}
                </div>
              </div>
            </div>
          </motion.div>
        ))}
        {!mechanics.length && (
          <div className="col-span-full text-center text-muted-foreground text-xs py-16 bg-card border border-dashed border-border rounded-md" data-testid="mechanics-empty">
            No mechanics yet — add your first crew member.
          </div>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#0C100D] border-primary/30 text-foreground max-w-md" data-testid="mechanic-modal">
          <DialogHeader>
            <DialogTitle className="font-display tracking-tight">
              {editing ? "EDIT MECHANIC" : "ADD MECHANIC"}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={save} className="space-y-4" data-testid="mechanic-form">
            <div>
              <label className="text-[10px] tracking-[0.25em] text-muted-foreground block mb-2">NAME</label>
              <Input data-testid="mechanic-name-input" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-secondary/60 border-input rounded-sm h-11 text-sm" />
            </div>
            {!editing && (
              <div>
                <label className="text-[10px] tracking-[0.25em] text-muted-foreground block mb-2">EMAIL</label>
                <Input data-testid="mechanic-email-input" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="bg-secondary/60 border-input rounded-sm h-11 text-sm" />
              </div>
            )}
            <div>
              <label className="text-[10px] tracking-[0.25em] text-muted-foreground block mb-2">
                {editing ? "NEW PASSWORD (LEAVE BLANK TO KEEP)" : "PASSWORD"}
              </label>
              <Input data-testid="mechanic-password-input" type="password" required={!editing} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="bg-secondary/60 border-input rounded-sm h-11 text-sm" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-[10px] tracking-[0.25em] text-muted-foreground block mb-2">RANK</label>
                <select
                  data-testid="mechanic-rank-select"
                  value={form.rank}
                  onChange={(e) => setForm({ ...form, rank: e.target.value })}
                  className="w-full bg-secondary/60 border border-input rounded-sm h-11 px-3 text-sm text-foreground"
                >
                  {RANKS.map((r) => (
                    <option key={r} value={r}>{r}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-[10px] tracking-[0.25em] text-muted-foreground block mb-2">COMMISSION %</label>
                <Input data-testid="mechanic-rate-input" type="number" min="0" max="100" step="0.5" required value={form.commission_rate} onChange={(e) => setForm({ ...form, commission_rate: e.target.value })} className="bg-secondary/60 border-input rounded-sm h-11 text-sm" />
              </div>
            </div>
            <label
              data-testid="mechanic-payout-toggle"
              className={`flex items-center gap-3 border rounded-sm px-3.5 py-3 cursor-pointer transition-colors ${form.can_payout ? "border-primary/50 bg-primary/10" : "border-border bg-secondary/40 hover:border-primary/30"}`}
            >
              <input
                type="checkbox"
                data-testid="mechanic-payout-checkbox"
                checked={!!form.can_payout}
                onChange={(e) => setForm({ ...form, can_payout: e.target.checked })}
                className="w-4 h-4 accent-[#00FF66]"
              />
              <span>
                <span className="block text-[10px] tracking-[0.2em] text-foreground font-semibold">ALLOW PAYOUT ACCESS</span>
                <span className="block text-[10px] text-muted-foreground mt-0.5">
                  Can open the payout queue and pay out the crew's commission.
                </span>
              </span>
            </label>
            <label
              data-testid="mechanic-saleslog-toggle"
              className={`flex items-center gap-3 border rounded-sm px-3.5 py-3 cursor-pointer transition-colors ${form.can_view_sales ? "border-primary/50 bg-primary/10" : "border-border bg-secondary/40 hover:border-primary/30"}`}
            >
              <input
                type="checkbox"
                data-testid="mechanic-saleslog-checkbox"
                checked={!!form.can_view_sales}
                onChange={(e) => setForm({ ...form, can_view_sales: e.target.checked })}
                className="w-4 h-4 accent-[#00FF66]"
              />
              <span>
                <span className="block text-[10px] tracking-[0.2em] text-foreground font-semibold">ALLOW SALES LOG ACCESS</span>
                <span className="block text-[10px] text-muted-foreground mt-0.5">
                  Can open the Sales Log and see every sale by the whole crew.
                </span>
              </span>
            </label>
            {editing && (
              <div className="border border-amber-400/25 bg-amber-400/5 rounded-sm p-3.5">
                <div className="text-[9px] tracking-[0.25em] text-amber-400/80 mb-3">BALANCE OVERRIDE — SET EXACT NUMBERS</div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-[10px] tracking-[0.25em] text-muted-foreground block mb-2">LIFETIME SALES $</label>
                    <Input data-testid="mechanic-lifetime-input" type="number" min="0" step="1" value={form.lifetime_sales} onChange={(e) => setForm({ ...form, lifetime_sales: e.target.value })} className="bg-secondary/60 border-input rounded-sm h-11 text-sm" />
                  </div>
                  <div>
                    <label className="text-[10px] tracking-[0.25em] text-muted-foreground block mb-2">COMMISSION OWED $</label>
                    <Input data-testid="mechanic-owed-input" type="number" min="0" step="1" value={form.owed} onChange={(e) => setForm({ ...form, owed: e.target.value })} className="bg-secondary/60 border-input rounded-sm h-11 text-sm" />
                  </div>
                </div>
                <p className="text-[10px] text-muted-foreground mt-2.5 leading-relaxed">
                  Any difference is written to the books as a "Balance Adjustment" entry, so the Sales Log stays honest.
                </p>
              </div>
            )}
            <Button data-testid="mechanic-save-btn" type="submit" disabled={busy} className="w-full bg-primary text-primary-foreground hover:bg-primary/90 font-display text-[11px] tracking-[0.25em] h-12 rounded-sm">
              {busy ? "SAVING…" : editing ? "SAVE CHANGES" : "ADD TO ROSTER"}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
